import type { CachedData } from '../constants/types/banner-type';
import { CACHE_KEYS } from '../constants/cache';

/**
 * 배너 데이터 캐싱을 위한 유틸리티 객체
 * localStorage를 사용하여 배너 데이터와 ETag를 관리
 * 주의사항
 * 1. localStorage는 문자열만 저장 가능하므로 객체는 JSON.stringify로 변환
 * 2. localStorage는 용량 제한이 있으므로 큰 데이터는 주의
 * 3. 브라우저의 개인정보 보호 모드에서는 localStorage 사용 불가능할 수 있음
 */
export const cacheUtils = {
  /**
   * localStorage에서 캐시된 배너 데이터를 조회
   * @returns {CachedData | null} 캐시된 배너 데이터 또는 캐시가 없는 경우 null
   * @example
   * const cachedData = cacheUtils.getBannerCache();
   * if (cachedData) {
   *   const { banners, timestamp } = cachedData;
   * }
   */
  getBannerCache(): CachedData | null {
    const cachedData = localStorage.getItem(CACHE_KEYS.BANNER);
    return cachedData ? JSON.parse(cachedData) : null;
  },

  /**
   * localStorage에서 배너 데이터의 ETag를 조회
   * ETag : 서버의 리소스가 변경되었는지 확인하는데 사용되는 식별자
   * @returns {string | null} 저장된 ETag 값 또는 캐시가 없는 경우 null
   * @example
   * const etag = cacheUtils.getEtagCache();
   * if (etag) {
   *   // If-None-Match 헤더에 etag 사용
   * }
   */
  getEtagCache(): string | null {
    return localStorage.getItem(CACHE_KEYS.BANNER_ETAG);
  },

  /**
   * 배너 데이터를 localStorage에 캐시
   * @param {CachedData} data 캐시할 배너 데이터 (배너 목록과 타임스탬프 포함)
   * @example
   * cacheUtils.setBannerCache({
   *   banners: newBanners,
   *   timestamp: Date.now()
   * });
   */
  setBannerCache(data: CachedData): void {
    localStorage.setItem(CACHE_KEYS.BANNER, JSON.stringify(data));
  },

  /**
   * 서버에서 받은 ETag 값을 localStorage에 저장
   * @param {string} etag 서버 응답 헤더에서 받은 ETag 값
   * @example
   * const newEtag = response.headers.etag;
   * if (newEtag) {
   *   cacheUtils.setEtagCache(newEtag);
   * }
   */
  setEtagCache(etag: string): void {
    localStorage.setItem(CACHE_KEYS.BANNER_ETAG, etag);
  },
};
